import { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { SearchBar } from '../../presentation-components/display-elements-components/search-bar';
import MfaService from '../../../shared/services/mfa/mfa.service';
import { IPartnerClientMfa, IPartnerMfa } from '../../../shared/services/mfa/mfa.model';
import { TwoFactorSettings } from '../../presentation-components/settings-components/two-factor-settings';
import { MfaEnforcementSection } from '../../presentation-components/settings-components/mfa-enforcement-section';

// Partner "Security" area: the partner user's own two-factor settings, then one
// card per client organisation with the same enforce toggle and user table an
// org admin sees. The client's own admin can still change their enforcement.
export const PartnerSecurityPage = () => {
  const mfaService = useMemo(() => new MfaService(), []);
  const userNotifications = useContext(UserNotificationsContext);

  const [data, setData] = useState<IPartnerMfa | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'personal' | 'clients'>('personal');

  // Filter by company name or by a user's name/email. A company-name match keeps
  // the whole org; a person match keeps only their client(s), showing just the
  // matching people so the partner can find them quickly.
  const visibleClients = useMemo<IPartnerClientMfa[]>(() => {
    if (!data) return [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return data.clients;
    return data.clients
      .map((client) => {
        const companyMatches = (client.name || '').toLowerCase().includes(query);
        if (companyMatches) return client;
        const matchingUsers = client.users.filter((u) =>
          `${u.firstName} ${u.surname}`.toLowerCase().includes(query) || (u.email || '').toLowerCase().includes(query));
        return matchingUsers.length ? { ...client, users: matchingUsers } : null;
      })
      .filter((client): client is IPartnerClientMfa => client !== null);
  }, [data, searchQuery]);

  const refresh = () => mfaService.getPartnerMfa()
    .then(setData)
    .catch(() => { /* the http interceptor handles auth errors */ })
    .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorToast = (err?: any) => userNotifications.openSnackbar({
    open: true,
    message: err?.response?.data?.message || 'There was an error, please try again',
    backgroundColor: 'text.primary',
  });

  const makeToggleHandler = (organisationId: string) => async (next: boolean) => {
    setBusy(true);
    try {
      const res = await mfaService.setPartnerClientEnforcement(organisationId, next);
      await refresh();
      userNotifications.openSnackbar({
        open: true,
        message: next
          ? `Two-factor authentication is now required${res?.movedToEmail ? ` (${res.movedToEmail} moved to email)` : ''}`
          : 'Two-factor authentication is no longer required',
      });
    } catch (err) {
      errorToast(err);
    } finally {
      setBusy(false);
    }
  };

  const onSetToEmail = async (userId: string) => {
    setBusy(true);
    try {
      await mfaService.partnerSetUserToEmail(userId);
      await refresh();
      userNotifications.openSnackbar({ open: true, message: 'User set to email codes' });
    } catch (err) {
      errorToast(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageOuterContainer>
      <PageHeading title="Two-factor authentication" hasTabs={false} />
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={(_, value) => { if (value) setActiveTab(value); }}
          aria-label="security sections"
        >
          <ToggleButton value="personal" aria-label="your settings" sx={{ width: 200, py: 0.75 }}>
            <Typography variant="body1" sx={{ color: 'text.primary', textTransform: 'none' }}>Your settings</Typography>
          </ToggleButton>
          <ToggleButton value="clients" aria-label="client organisations" sx={{ width: 200, py: 0.75 }}>
            <Typography variant="body1" sx={{ color: 'text.primary', textTransform: 'none' }}>Client organisations</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {activeTab === 'personal' ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>Your two-factor authentication</Typography>
            <TwoFactorSettings />
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {loading ? (
            <CircularProgress size={22} />
          ) : !data || !data.clients.length ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>You have no client organisations yet.</Typography>
          ) : (
            <>
              <Box sx={{ maxWidth: 420 }}>
                <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
              </Box>
              {visibleClients.length ? (
                visibleClients.map((client) => (
                  <Card key={client.organisationId} variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{client.name}</Typography>
                      <MfaEnforcementSection
                        enforceMfa={client.enforceMfa}
                        users={client.users}
                        busy={busy}
                        onToggleEnforcement={makeToggleHandler(client.organisationId)}
                        onSetToEmail={onSetToEmail}
                      />
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>No clients or people match your search.</Typography>
              )}
            </>
          )}
        </Stack>
      )}
    </PageOuterContainer>
  );
};
